import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getCmsListUrl, getCmsDetailUrl } from '../config/apiConfig';
import './CMSPage.css';

// Helper to normalize and clean strings for robust matching
const normalizeString = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[-_\s]+/g, '') // Remove all hyphens, underscores, and spaces
    .trim();
};

// Helper to format identifiers into clean titles
const getCleanTitle = (str) => {
  if (!str) return '';
  return str
    .replace(/[_-]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CMSPage = ({ identifier: propIdentifier }) => {
  const { slug } = useParams();
  const identifier = propIdentifier || slug || '';
  
  const [contentData, setContentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);

  useEffect(() => {
    const fetchCMS = async () => {
      setLoading(true);
      setError(null);
      const logs = [];

      logs.push(`Initializing CMS fetch. Prop Identifier: "${propIdentifier || ''}", URL Slug: "${slug || ''}"`);
      logs.push(`Resolved search identifier: "${identifier}"`);

      try {
        // Step 1: Fetch CMS pages list
        const listUrl = getCmsListUrl();
        logs.push(`Fetch list URL -> ${listUrl}`);
        
        const listResponse = await fetch(listUrl);
        logs.push(`List Response Status -> ${listResponse.status} ${listResponse.statusText}`);
        
        if (!listResponse.ok) {
          throw new Error(`Failed to load CMS list: status ${listResponse.status}`);
        }
        
        const listData = await listResponse.json();
        logs.push(`List JSON parsed. Keys: ${Object.keys(listData).join(', ')}`);
        
        let list = null;
        if (listData && typeof listData === 'object') {
          if (listData.payload?.data && Array.isArray(listData.payload.data)) {
            list = listData.payload.data;
          } else if (listData.payload && Array.isArray(listData.payload)) {
            list = listData.payload;
          } else if (listData.data && Array.isArray(listData.data)) {
            list = listData.data;
          } else if (Array.isArray(listData)) {
            list = listData;
          }
        }
        
        if (!list) {
          throw new Error("No CMS list found in API response payload.");
        }
        
        logs.push(`CMS list loaded successfully: ${list.length} items`);

        const normSearchSlug = normalizeString(slug);
        const normSearchIdentifier = normalizeString(identifier);
        logs.push(`Normalized search keys: slug="${normSearchSlug}", identifier="${normSearchIdentifier}"`);
        
        // Find the page matching the identifier or slug with normalized strings
        const matchingItem = list.find(item => {
          const normItemIdentifier = normalizeString(item.identifier);
          const normItemTitle = normalizeString(item.title);
          
          const isMatch = 
            (item.identifier && (normItemIdentifier === normSearchSlug || normItemIdentifier === normSearchIdentifier)) ||
            (item.title && (normItemTitle === normSearchSlug || normItemTitle === normSearchIdentifier)) ||
            item._id === identifier ||
            item._id === slug;

          if (isMatch) {
            logs.push(`Match found! ID: ${item._id}, Identifier: "${item.identifier}", Title: "${item.title}"`);
          }
          return isMatch;
        });
        
        if (!matchingItem) {
          // Log available items for better error diagnostic in the UI logs
          const listDetails = list.map(item => `[ID: ${item._id}, Ident: "${item.identifier}", Title: "${item.title}"]`).join(', ');
          logs.push(`Available items: ${listDetails}`);
          throw new Error(`Page not found in CMS list for identifier/slug: "${identifier}"`);
        }
        
        const cmsId = matchingItem._id;
        
        // Step 2: Fetch detailed page content by ID
        const detailUrl = getCmsDetailUrl(cmsId);
        logs.push(`Fetch detail URL -> ${detailUrl}`);
        
        const detailResponse = await fetch(detailUrl);
        logs.push(`Detail Response Status -> ${detailResponse.status} ${detailResponse.statusText}`);
        
        if (!detailResponse.ok) {
          throw new Error(`Failed to load page content: status ${detailResponse.status}`);
        }
        
        const detailData = await detailResponse.json();
        logs.push(`Detail JSON parsed. Keys: ${Object.keys(detailData).join(', ')}`);
        
        const detailPayload = detailData.payload || detailData.data || detailData;
        const bodyContent = detailPayload.description || detailPayload.content || detailPayload.body || detailPayload.text || detailPayload.htmlContent;
        
        logs.push(`Content check: description=${!!detailPayload.description}, content=${!!detailPayload.content}, body=${!!detailPayload.body}, htmlContent=${!!detailPayload.htmlContent}`);
        
        setContentData({
          title: detailPayload.title ? getCleanTitle(detailPayload.title) : getCleanTitle(matchingItem.title || matchingItem.identifier || identifier),
          lastUpdated: detailPayload.updatedAt 
            ? new Date(detailPayload.updatedAt).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' })
            : "Recently updated",
          body: bodyContent || `Page loaded but has no content fields. Raw details:\n\n${JSON.stringify(detailPayload, null, 2)}`
        });
        
      } catch (e) {
        logs.push(`Error -> ${e.message}`);
        
        // Check if list loaded successfully but matching item was not found
        const isPageNotFound = logs.some(l => l.includes("Page not found in CMS list"));
        if (isPageNotFound) {
          setError(`Page Not Found: The requested policy page "${identifier || slug}" does not exist.`);
        } else {
          setError("Failed to load policy content from the server.");
        }
        console.error("CMS Page fetch failed:", e);
      } finally {
        setDebugLogs(logs);
        setLoading(false);
      }
    };

    fetchCMS();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [identifier, slug, propIdentifier]);

  // Page title and metadata dynamic configuration
  useEffect(() => {
    const title = contentData ? contentData.title : getCleanTitle(identifier || slug);
    document.title = `${title} | Zoplop - Ride Safe. Ride Local.`;
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `Read the official Zoplop ${title}. Learn about our guidelines, community values, and driver requirements in Canada.`);
    }
  }, [contentData, identifier, slug]);

  const isHTML = (str) => {
    return /<[a-z][\s\S]*>/i.test(str);
  };

  const renderContent = () => {
    if (!contentData || !contentData.body) return null;

    if (isHTML(contentData.body)) {
      return <div className="cms-rendered-html" dangerouslySetInnerHTML={{ __html: contentData.body }} />;
    } else {
      return (
        <div className="cms-rendered-text">
          {contentData.body.split('\n\n').map((paragraph, idx) => {
            const cleanPara = paragraph.trim();
            if (!cleanPara) return null;

            if (cleanPara.startsWith('[Debug Info]') || cleanPara.startsWith('API Record matches')) {
              return <pre key={idx} className="cms-debug-pre">{cleanPara}</pre>;
            }
            
            // Check if paragraph starts with a heading pattern, e.g., "1. Introduction" or "1. "
            if (/^\d+\.\s+/.test(cleanPara)) {
              return <h2 key={idx} className="cms-section-heading">{cleanPara}</h2>;
            }
            
            // Check if paragraph starts with bullet characters
            if (cleanPara.startsWith('•') || cleanPara.startsWith('-') || cleanPara.startsWith('*')) {
              const items = cleanPara.split('\n').map(item => item.trim()).filter(Boolean);
              return (
                <ul key={idx} className="cms-list">
                  {items.map((item, itemIdx) => (
                    <li key={itemIdx} className="cms-list-item">
                      {item.replace(/^[•\-\*\s]+/, '')}
                    </li>
                  ))}
                </ul>
              );
            }
            
            return <p key={idx} className="cms-paragraph">{cleanPara}</p>;
          })}
        </div>
      );
    }
  };

  return (
    <div className="cms-page-wrapper">
      <Header />
      
      <main className="cms-main-content">
        <div className="cms-layout-container">
          <article className="cms-body-card">
            {loading ? (
              <div className="cms-content-loader">
                <div className="cms-shimmer cms-shimmer-title" />
                <div className="cms-shimmer cms-shimmer-body" />
                <div className="cms-shimmer cms-shimmer-body" />
                <div className="cms-shimmer cms-shimmer-body" />
              </div>
            ) : error ? (
              <div className="cms-error-container">
                <h1 className="cms-page-title">{getCleanTitle(identifier || slug)}</h1>
                <div className="cms-title-underline" />
                <p className="cms-error-message">{error}</p>
                
                {/* Embedded dynamic logs to troubleshoot on user side */}
                <div className="cms-console-debug">
                  <h3>Connection Debug Logs</h3>
                  <div className="cms-console-pre">
                    {debugLogs.map((log, i) => (
                      <div key={i} className="cms-console-line">{log}</div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="cms-content-container">
                <h1 className="cms-page-title">{contentData.title}</h1>
                <div className="cms-title-underline" />
                {renderContent()}
              </div>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CMSPage;
